<script setup>
import { formatDistanceToNow } from 'date-fns'
import { countries } from '~/utils'

definePageMeta({
  layout: 'public'
})

const { bg } = useBackground()
bg.value = 'dimmed'

const { $pb } = useNuxtApp()

const route = useRoute()
const slug = route.params.slug

const offset = ref(0)
const limit = ref(10)

const { data, error, status, refresh } = await useAsyncData(`startup-${slug}`, async () => {
  const startup = await $pb.collection('startups').getFirstListItem(`slug = '${slug}'`, {
    expand: 'location'
  })

  const jobs = await $pb.collection('jobs_view').getList(offset.value, limit.value, {
    filter: `startup = "${startup.id}"`,
    expand: 'location,categories,tags'
  })

  return {
    startup,
    jobs: jobs.items
  }
})

const head = {
  title: `${data.value.startup.name} | BuiltByFew`,
  description: `Based in ${data.value?.startup?.expand?.location.city}`
}

defineOgImageComponent('OgImage', {
  title: head.title,
  description: head.description,
})

function heroPreview() {
  if (data.value.startup.hero) {
    return $pb.files.getURL(data.value.startup, data.value.startup.hero)
  }
}

function logoPreview() {
  if (data.value.startup.logo) {
    return $pb.files.getURL(data.value.startup, data.value.startup.logo)
  }
}

function getCountryName(countryCode) {
  return countries.find(country => country.value === countryCode).name
}

const items = ref([
  {
    label: 'Startups',
    icon: 'i-lucide-building-2',
    to: '/startups'
  },
  {
    label: data.value.startup.name,
    to: `/startups/${data.value.startup.slug}`
  }
])

onMounted(() => {
  if (import.meta.client) {
    nextTick(() => {
      if (bg.value !== 'dimmed') {
        bg.value = 'dimmed'
      }
    })
  }
})
</script>

<template>
  <div class="px-0">
    <div v-if="status === 'pending'">
      <div class="flex flex-col justify-center items-center h-full">
        <UIcon name="i-heroicons-arrow-path" class="h-5 w-5 animate-spin" />
        <div class="mt-2 text-sm text-gray-500">
          Fetching your startup data...
        </div>
      </div>
    </div>

    <UError v-else-if="error"
      :status="400"
      name="Whoops!"
      message="We couldn't find the startup you were looking for or something went wrong."
    />

    <UContainer v-else class="mx-auto">
      <div class="mt-4 p-4 bg-(--ui-bg-muted)">
        <UBreadcrumb :items="items" />
      </div>

      <div class="mt-6 mb-8 p-4 grid grid-cols-1 gap-4 bg-(--ui-bg-muted) rounded-md border border-(--ui-border)">
        <div v-if="data?.startup?.hero" class="relative mt-4 rounded-xl overflow-hidden">
          <img
            :src="heroPreview(data.startup)"
            :alt="data.startup.name"
            class="w-full h-96 object-cover"
          >
        </div>

        <div class="grid grid-cols-12 items-start pt-4 gap-4 text-sm">
          <div class="col-span-12 md:col-span-4 flex flex-col items-start gap-2 w-full">
            <div class="flex items-center gap-2">
              <div class="relative w-16 h-16 overflow-hidden object-contain bg-neutral border border-(--ui-border-muted)">
                <img :src="logoPreview()" :alt="data.startup.name" class="w-full h-full object-contain" />
              </div>
              <div class="max-w-64">
                <h1 class="normal text-2xl font-bold truncate">
                  {{ data.startup.name }}
                </h1>

                <div class="flex items-center gap-2 pt-2 text-gray-500 dark:text-gray-400">
                  <UIcon name="i-heroicons-map-pin" class="w-5 h-5" />
                  {{ data.startup.expand.location.city }}, {{ getCountryName(data.startup.expand.location.country) }}
                </div>
              </div>
            </div>
            <div class="text-sm text-(--ui-text-muted) flex items-center gap-2 pt-2">
              <a
                v-if="data.startup.website"
                :href="data.startup.website"
                target="_blank"
                class="underline"
              >
                {{ data.startup.website }}
              </a>
              <UIcon name="i-lucide-external-link" class="w-4 h-4" />
            </div>
          </div>

          <div class="col-span-12 md:col-span-8">
            <div v-html="data.startup.description" />
          </div>
        </div>

        <div v-if="data?.jobs?.length > 0" class="flex flex-col items-start">
          <span class="text-sm leading-tight text-(--ui-text-muted)">
            Jobs available right now...
          </span>
        </div>
        <div v-else class="flex flex-col items-start">
          <span class="text-sm leading-tight text-(--ui-text-muted)">
            No jobs available at the moment.
          </span>
        </div>

        <div v-for="job in data?.jobs" :key="job.id">
          <UCard>
            <div class="grid grid-cols-8 grow">
              <div class="col-span-8 md:col-span-2 flex flex-col items-start py-2 md:py-0">
                <div class="text-base font-medium">
                  <NuxtLink
                    :to="`/startups/${data?.startup?.slug}/${job.id}`"
                    class="hover:underline"
                  >
                    {{ job.title }}
                  </NuxtLink>
                </div>
                <div class="text-sm text-gray-500 dark:text-gray-400">
                  {{ job.shortDescription }}
                </div>
                <div class="pt-1 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <UIcon name="i-heroicons-map-pin" class="w-5 h-5" />
                  {{ job.expand?.location?.city }}, {{ getCountryName(job.expand?.location?.country) }}
                </div>
              </div>

              <div class="col-span-8 md:col-span-2 flex flex-wrap items-center gap-1 py-2 md:py-0">
                <div
                  v-for="category in job.expand?.categories"
                  :key="category.id"
                >
                  <UBadge
                    :label="category.name"
                    color="neutral"
                    variant="subtle"
                  />
                </div>
              </div>

              <div class="col-span-8 md:col-span-2 flex flex-wrap items-center gap-1 py-2 md:py-0">
                <div
                  v-for="tag in job.expand?.tags"
                  :key="tag.id"
                >
                  <UBadge
                    :label="tag.name"
                    color="primary"
                    variant="subtle"
                  />
                </div>
              </div>


              <div class="col-span-4 md:col-span-1 flex justify-start items-center gap-1 text-xs text-gray-500 dark:text-gray-400 py-2 md:py-0">
                <UIcon name="i-heroicons-calendar-days" class="w-5 h-5" />
                Listed<br>
                {{ formatDistanceToNow(job.publishAt || new Date()) }} ago
              </div>

              <div class="col-span-4 md:col-span-1 flex items-center justify-end  py-2 md:py-0">
                <div>
                  <UButton
                    :to="`/startups/${data?.startup?.slug}/${job.id}`"
                    label="Apply Now"
                    color="success"
                    variant="solid"
                  />
                </div>
              </div>
            </div>
          </UCard>
        </div>
      </div>
    </UContainer>
  </div>
</template>

<style>
.content p {
  padding: 1rem 0;
}
</style>
